import React, { useState, useEffect, useRef } from 'react';
import { Briefcase, History, Check, Lightbulb, Brain, UploadCloud, Shield, FileCheck, FileX, BarChart2, FileText, X, AlignLeft, GraduationCap, Sparkles, Loader2, Info, Layers, Rocket, Lock } from 'lucide-react';
import HardwareCheck from '../components/AiAgents/HardwareCheck';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Loader from '../components/Loader';
import Chatbot from '../components/AiAgents/Chatbot';
import AvatarModal from '../components/AiAgents/AvatarModal';
import PerformanceReport from '../components/AiAgents/PerformanceReport';
import ResumeScorerResults from '../components/AiAgents/ResumeScorerResults';
import StudentLayout from '../components/StudentLayout';
import './AiAgents.css';


function SidebarAiInterviewer({ embedded = false }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    topic: '',
    resume: null // Used for Tutor mode notes
  });

  const [resumeFile, setResumeFile] = useState(null); // Used for initial upload
  const [hasResume, setHasResume] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const [submitted, setSubmitted] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const mode = 'interviewer';
  const [showAvatar, setShowAvatar] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionMeta, setSessionMeta] = useState(null);

  const [beyAgentId, setBeyAgentId] = useState('');
  const [level, setLevel] = useState('Intermediate');
  const [stage, setStage] = useState('Basic'); // 'Basic' (none) | 'Pro' (Resume Scorer) | 'Enterprise' (Resume Scorer & Counsellor)
  const [hoveredStage, setHoveredStage] = useState(null);
  const [hoveredLevel, setHoveredLevel] = useState(null);
  const [hoveredJdMode, setHoveredJdMode] = useState(null);

  const [interviewStep, setInterviewStep] = useState('setup');
  const [beyEmbedUrl, setBeyEmbedUrl] = useState('');
  const [resolvedEmbedUrl, setResolvedEmbedUrl] = useState('');
  const [agentStatus, setAgentStatus] = useState('unchecked');
  const [scorerResult, setScorerResult] = useState(null);
  const [runningScorer, setRunningScorer] = useState(false);
  const [showScorerModal, setShowScorerModal] = useState(false);

  // Companion feature: AI Counsellor state (unlocked on Hard level)
  const [showCounsellorAvatar, setShowCounsellorAvatar] = useState(false);
  const [counsellorSessionId, setCounsellorSessionId] = useState(null);
  const [counsellorEmbedUrl, setCounsellorEmbedUrl] = useState('');
  const [launchingCounsellor, setLaunchingCounsellor] = useState(false);

  const [latestReport, setLatestReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showHistoryInitially, setShowHistoryInitially] = useState(false);


  const [aiUsage, setAiUsage] = useState(0);
  const [aiLimit, setAiLimit] = useState(3600);

  const [jobRoles, setJobRoles] = useState([]);
  const [selectedJobRole, setSelectedJobRole] = useState('');
  const [loadingJobRoles, setLoadingJobRoles] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');

  // Job Description state
  const [jdInputMode, setJdInputMode] = useState('none'); // 'none' | 'text' | 'file'
  const [jdText, setJdText] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [jdFileName, setJdFileName] = useState('');
  const [jdMismatchError, setJdMismatchError] = useState('');
  const [isDraggingJd, setIsDraggingJd] = useState(false);

  const jdFileInputRef = useRef(null);

  // Additional Supporting Documents state (Optional, max 3)
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const additionalDocsInputRef = useRef(null);

  // AI Counsellor previous interview report state (Optional)
  const [counsellorReportFile, setCounsellorReportFile] = useState(null);
  const counsellorReportInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isRoleDropdownOpen && !event.target.closest('.custom-role-dropdown-container')) {
        setIsRoleDropdownOpen(false);
        setRoleSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isRoleDropdownOpen]);

  const [toast, setToast] = useState(null);

  const formatErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
      return err.map(item => formatErrorMessage(item)).filter(Boolean).join('; ');
    }
    if (typeof err === 'object') {
      if (err.msg) return err.msg;
      if (err.detail) return formatErrorMessage(err.detail);
      if (err.message) return formatErrorMessage(err.message);
      try {
        return JSON.stringify(err);
      } catch (e) {
        return 'An error occurred.';
      }
    }
    return String(err);
  };

  const showToast = (message, type = 'error') => {
    const formattedMsg = formatErrorMessage(message);
    setToast({ message: formattedMsg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchLatestReport = async (targetMode = mode) => {
    setLoadingReport(true);
    try {
      const res = await api.get(`/ai-agents/reports/latest?mode=${targetMode}`);
      if (res.data && res.data.report) {
        setLatestReport(res.data);
      } else {
        setLatestReport(null);
      }
    } catch (e) {
      console.error('Failed to fetch latest report:', e);
    } finally {
      setLoadingReport(false);
    }
  };

  const [studentProfile, setStudentProfile] = useState(null);

  const handleSessionEnded = (resData) => {
    if (resData) {
      const reportContent = resData.final_index || resData.report || resData;
      setLatestReport({
        sessionId: resData.sessionId || currentSessionId,
        mode: resData.mode || mode,
        createdAt: resData.createdAt || new Date().toISOString(),
        completedAt: resData.completedAt || new Date().toISOString(),
        studentName: resData.studentName || studentProfile?.full_name || studentProfile?.name,
        institutionName: resData.institutionName || studentProfile?.institution_name || studentProfile?.university_name,
        usn: resData.usn || studentProfile?.usn,
        report: reportContent
      });
      showToast(`${mode.charAt(0).toUpperCase() + mode.slice(1)} completed! AI Performance Evaluation report is ready.`, "info");
    } else {
      fetchLatestReport(mode);
      setInterviewStep('setup');
    }
  };

  useEffect(() => {
    fetchProfileAndConfig();
  }, []);

  const fetchProfileAndConfig = async () => {
    try {
      // Fetch Config (restricted to non-students)
      try {
        const configRes = await api.get('/ai-agents/config');
        if (configRes.data) {
          setBeyAgentId((configRes.data.beyAgentId || '').trim());
          setBeyEmbedUrl((configRes.data.beyEmbedUrl || '').trim());
          setResolvedEmbedUrl((configRes.data.resolvedEmbedUrl || '').trim());
          setAgentStatus((configRes.data.agentStatus || 'unchecked').trim());
        }
      } catch (cfgErr) {
        // Config restricted or unavailable; use default state
      }

      // Fetch Profile
      const profileRes = await api.get('/student/profile/me');
      if (profileRes.data) {
        setStudentProfile(profileRes.data);
        const allocatedList = profileRes.data.allocated_stages || (profileRes.data.allocated_stage ? profileRes.data.allocated_stage.split(',').map(s => s.trim()).filter(Boolean) : ['Basic']);
        if (allocatedList.length > 0 && !allocatedList.includes(stage)) {
          setStage(allocatedList[0]);
        }
        if (profileRes.data.resume_text) {
          setHasResume(true);
        }
      }

      // Fetch Usage
      try {
        const usageRes = await api.get('/ai-agents/usage');
        setAiUsage(usageRes.data.usage);
        setAiLimit(usageRes.data.limit);
      } catch (e) {
        console.error("Failed to fetch AI usage", e);
      }

      // Fetch Dynamic Job Roles for Interviewer
      await fetchJobRoles();
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        showToast("AI Interviewer is not available for your account.", "error");
        const role = (JSON.parse(localStorage.getItem('user') || '{}').role) || 'student';
        const home = { admin: '/admin-dashboard', system_admin: '/admin-dashboard', university: '/uni-dashboard', student: '/student-dashboard', faculty: '/faculty-dashboard', tpo: '/tpo-dashboard', pop: '/pop-dashboard' }[role] || '/student-dashboard';
        navigate(home);
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchJobRoles = async () => {
    try {
      setLoadingJobRoles(true);
      const rolesRes = await api.get('/ai-agents/job-roles');
      if (rolesRes.data && Array.isArray(rolesRes.data.roles)) {
        setJobRoles(rolesRes.data.roles);
      }
    } catch (e) {
      console.error("Failed to fetch job roles", e);
    } finally {
      setLoadingJobRoles(false);
    }
  };

  // Re-fetch job roles whenever switching to Interviewer mode
  useEffect(() => {
    if (mode === 'interviewer') {
      fetchJobRoles();
    }
  }, [mode]);

  useEffect(() => {
    let interval = null;
    let timeout = null;
    if (showAvatar && aiUsage < aiLimit) {
      // 10 second grace period before starting the usage timer
      timeout = setTimeout(() => {
        interval = setInterval(async () => {
          try {
            const res = await api.post('/ai-agents/usage/heartbeat', { seconds: 10 });
            setAiUsage(res.data.usage);
            if (res.data.usage >= res.data.limit) {
              setShowAvatar(false);
              showToast("AI Time Limit Reached. Please request an extension.", "error");
            }
          } catch (e) {
            console.error("Heartbeat failed", e);
          }
        }, 10000);
      }, 10000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [showAvatar, aiUsage, aiLimit]);

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}m ${s}s`;
  };

  const handleInitialResumeUpload = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      showToast("Please select a PDF file.", "error");
      return;
    }

    setUploadingResume(true);
    try {
      const data = new FormData();
      data.append('resume', resumeFile);

      await api.post('/ai-agents/upload-resume', data);
      setHasResume(true);
    } catch (error) {
      console.error("Upload error:", error);
      showToast(error.response?.data?.detail || "Failed to upload resume.", "error");
    } finally {
      setUploadingResume(false);
    }
  };

  const handleLevelChange = (selectedLevel) => {
    setLevel(selectedLevel);
  };

  const handleStageChange = (selectedStage) => {
    const rawAllocated = studentProfile?.allocated_stages || (studentProfile?.allocated_stage ? studentProfile.allocated_stage.split(',').map(s => s.trim()).filter(Boolean) : ['Basic']);
    const allocatedStages = Array.isArray(rawAllocated) ? rawAllocated : [rawAllocated];
    if (!allocatedStages.includes(selectedStage)) {
      showToast(`Your institution has not allocated you to the ${selectedStage} stage. Your allocated stage(s): ${allocatedStages.join(', ')}.`, 'warning');
      return;
    }
    setStage(selectedStage);
    if (selectedStage === 'Basic') {
      setScorerResult(null);
    }
  };

  const handleRunScorer = async () => {
    if (!hasResume) {
      showToast('No resume found. Please upload your resume in the Settings page first!', 'warning');
      return;
    }
    setRunningScorer(true);
    try {
      const response = await api.post('/ai-agents/resume/score');
      setScorerResult(response.data);
      setShowScorerModal(true);
      showToast('Resume evaluated successfully! Review your score and feedback.', 'info');
    } catch (error) {
      console.error('Resume scoring error:', error);
      showToast(error.response?.data?.detail || 'Failed to score resume. Please try again.', 'error');
    } finally {
      setRunningScorer(false);
    }
  };

  const handleLaunchCounsellor = async () => {
    if (!hasResume) {
      showToast('No resume found. Please upload your resume in the Settings page first!', 'warning');
      return;
    }
    setLaunchingCounsellor(true);
    try {
      const data = new FormData();
      if (counsellorReportFile) {
        data.append('interview_report', counsellorReportFile);
      }
      const response = await api.post('/agent-engine/counsellor/start', data);
      const result = response.data;
      setCounsellorSessionId(result.id);
      if (result.resolvedEmbedUrl) {
        setCounsellorEmbedUrl(result.resolvedEmbedUrl);
      }
      setShowCounsellorAvatar(true);
    } catch (error) {
      console.error('Counsellor launch error:', error);
      showToast(error.response?.data?.detail || 'Failed to launch AI Counsellor.', 'error');
    } finally {
      setLaunchingCounsellor(false);
    }
  };

  const handleAdditionalDocsSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 3 - additionalDocs.length;
    if (remaining <= 0) {
      showToast('You can attach a maximum of 3 supporting documents.', 'warning');
      return;
    }
    const valid = files.filter(f => {
      const n = f.name.toLowerCase();
      return n.endsWith('.pdf') || n.endsWith('.docx') || n.endsWith('.doc') || n.endsWith('.txt');
    });
    if (valid.length < files.length) {
      showToast('Some files were skipped: Only PDF, DOCX, DOC, or TXT formats are supported.', 'warning');
    }
    const toAdd = valid.slice(0, remaining);
    if (toAdd.length > 0) {
      setAdditionalDocs(prev => [...prev, ...toAdd]);
      showToast(`Added ${toAdd.length} supporting document(s).`, 'info');
    }
    if (additionalDocsInputRef.current) additionalDocsInputRef.current.value = '';
  };

  const handleRemoveAdditionalDoc = (index) => {
    setAdditionalDocs(prev => prev.filter((_, i) => i !== index));
  };

  const handleCounsellorReportSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.pdf') && !name.endsWith('.docx') && !name.endsWith('.doc') && !name.endsWith('.txt')) {
      showToast('Please select a valid PDF, DOCX, or TXT report file.', 'warning');
      return;
    }
    setCounsellorReportFile(file);
    showToast(`Report "${file.name}" attached for counsellor reference.`, 'info');
    if (counsellorReportInputRef.current) counsellorReportInputRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!level) {
      showToast('Please select a difficulty level before starting the interview.', 'error');
      return;
    }
    if (!hasResume) {
      showToast('No resume found. Please upload your resume in the Settings page first!', 'warning');
      return;
    }
    if (!selectedJobRole) {
      showToast('Please select a target job role before starting the interview.', 'error');
      return;
    }
    setInterviewStep('hardware');
  };

  const startLiveInterview = async () => {
    setInterviewStep('active');
    setSubmitted(true);
    setJdMismatchError('');
    try {
      let data = new FormData();
      data.append('job_role', selectedJobRole);
      data.append('level', level);
      data.append('stage', stage);

      // Attach Job Description if provided
      if (jdInputMode === 'text' && jdText.trim()) {
        data.append('job_description_text', jdText.trim());
      } else if (jdInputMode === 'file' && jdFile) {
        data.append('job_description_file', jdFile);
      }

      // Attach Additional Supporting Documents (up to 3)
      additionalDocs.forEach((doc, idx) => {
        data.append(`additional_doc_${idx}`, doc);
      });

      const response = await api.post('/sidebar-interviewer/start', data);
      const result = response.data;

      setCurrentSessionId(result.id);
      setSessionMeta(result);
      if (result.beyAgentId) {
        setBeyAgentId(result.beyAgentId);
      }
      if (result.resolvedEmbedUrl) {
        setResolvedEmbedUrl(result.resolvedEmbedUrl);
      }
      setShowAvatar(true);

    } catch (error) {
      console.error('Error:', error);
      const detail = error.response?.data?.detail || 'There was an error connecting to the server.';
      const isMismatch = error.response?.status === 400 && detail.toLowerCase().includes('mismatch');
      if (isMismatch) {
        setJdMismatchError(detail);
      } else {
        showToast(detail, 'error');
      }
      setInterviewStep('setup');
    } finally {
      setSubmitted(false);
    }
  };




  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume' && files.length > 0) {
      setFormData({ ...formData, resume: files[0] });
    } else if (name === 'topic') {
      setFormData({ ...formData, topic: value });
    }
  };

  const withMetadata = (baseUrl) => {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}metadata=${encodeURIComponent(
      JSON.stringify({ externalId: currentSessionId })
    )}`;
  };

  const avatarUrl = resolvedEmbedUrl
    ? withMetadata(resolvedEmbedUrl)
    : beyEmbedUrl
      ? withMetadata(beyEmbedUrl)
      : beyAgentId
        ? withMetadata(`https://bey.chat/${beyAgentId}`)
        : '';

  const counsellorAvatarUrl = counsellorEmbedUrl
    ? `${counsellorEmbedUrl}${counsellorEmbedUrl.includes('?') ? '&' : '?'}metadata=${encodeURIComponent(
        JSON.stringify({ externalId: counsellorSessionId })
      )}`
    : '';

  if (isLoadingProfile) {
    if (!embedded) return <Loader />;
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <h2>Loading Profile...</h2>
      </div>
    );
  }

  // --- Main Dashboard Screen ---
  const content = (
    <div className="ai-agents-root">
      <div className="ai-page-wrapper">

        {/* TOP FULL-WIDTH HEADER BOX */}
        <div className="ai-top-header-box">
          <div className="ai-header-top-row" style={{ flexDirection: 'column', gap: '24px' }}>
            <div className="ai-header-left">
              <span className="ai-badge-pill">
                <Brain size={14} /> AI AGENTS & TOOLS
              </span>
              <h2>AI Interviewer</h2>
              <p>
                Practice mock interviews with personalized AI feedback based on your resume.
              </p>
            </div>
            <div className="ai-header-right-actions">
              <div className="ai-usage-badge">
                AI Time Used: {formatTime(aiUsage)} / {Math.floor(aiLimit / 60)}m
              </div>
              <button
                className="ai-action-btn secondary"
                onClick={() => {
                  setShowHistoryInitially(true);
                }}
                title="View all previously saved reports"
                style={{ background: 'rgba(59, 130, 246, 0.15)', borderColor: 'rgba(59, 130, 246, 0.3)', color: '#60a5fa' }}
              >
                <History size={16} /> <span>Previous Reports</span>
              </button>
              <button
                className="ai-action-btn primary"
                onClick={() => {
                  showToast("Opening Profile to manage resume...", "info");
                  navigate('/student-dashboard', { state: { showSettings: true } });
                }}
                title="Manage saved resume"
              >
                <UploadCloud size={16} /> <span>Manage Resume</span>
              </button>
            </div>

            {/* Candidate Disclaimer regarding AI Feedback */}
            <div className="ai-candidate-disclaimer-banner">
              <Info size={16} className="ai-candidate-disclaimer-icon" />
              <div className="ai-candidate-disclaimer-text">
                <strong className="ai-candidate-disclaimer-title">Candidate Disclaimer:</strong>{' '}
                Feedback, evaluations, and scores generated by the AI Interviewer are automated and intended solely for mock interview practice, skill benchmarking, and developmental self-improvement. AI can make mistakes — always check and verify important information. Insights represent advisory guidance and do not constitute official hiring decisions or employment guarantees.
              </div>
            </div>
          </div>

          {/* NO TABS FOR SIDEBAR INTERVIEWER */}
        </div>

        {/* TWO-COLUMN BODY GRID */}
        <div className="ai-body-grid">
          {/* LEFT COLUMN CARD (340px) */}
          <div className="ai-sidebar-card">
            <div className="ai-sidebar-header">
              <h4>Interviewer Guide</h4>
              <span className="mode-badge">INTERVIEWER</span>
            </div>

            <div className="ai-sidebar-content">
              <div className="ai-info-section">
                <h5>Key Capabilities</h5>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
                    <FileText size={14} style={{ color: '#3b82f6', flexShrink: 0 }} /> Tailored questions from your resume
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
                    <AlignLeft size={14} style={{ color: '#3b82f6', flexShrink: 0 }} /> Job Description-aware interview questions
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
                    <Brain size={14} style={{ color: '#3b82f6', flexShrink: 0 }} /> Real-time mock interview simulation
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
                    <BarChart2 size={14} style={{ color: '#3b82f6', flexShrink: 0 }} /> Immediate scoring &amp; feedback
                  </li>
                  {(stage === 'Pro' || stage === 'Enterprise') && (
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#818cf8', fontWeight: '700' }}>
                      <FileCheck size={14} style={{ color: '#818cf8', flexShrink: 0 }} /> AI Resume Scorer Unlocked
                    </li>
                  )}
                  {stage === 'Enterprise' && (
                    <li style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#f59e0b', fontWeight: '700' }}>
                      <GraduationCap size={14} style={{ color: '#f59e0b', flexShrink: 0 }} /> AI Counsellor Unlocked
                    </li>
                  )}
                </ul>
              </div>

              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Lightbulb size={18} color="#eab308" />
                  <h5 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)', fontWeight: '700' }}>Pro Tip</h5>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  Upload a Job Description for the most targeted, role-specific questions. Ensure your mic and camera are ready before starting.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN CARD (MAIN INTERACTION WINDOW) */}
          <div className="ai-main-card">
            <div className="ai-main-empty-placeholder">
              <div className="ai-start-card">
              <div className="ai-start-card-header" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap',
                paddingBottom: '16px',
                borderBottom: '1px solid var(--border-color, #e2e8f0)'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.45rem', fontWeight: '800', color: 'var(--text-primary, #0f172a)' }}>
                    Start Interview
                  </h3>
                  <p style={{ margin: '4px 0 0', color: 'var(--text-secondary, #64748b)', fontSize: '0.875rem' }}>
                    Personalized AI mock interview calibrated to your target role and resume.
                  </p>
                </div>

                {/* Inline Resume Status Chip */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  background: hasResume ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${hasResume ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: hasResume ? '#059669' : '#dc2626'
                }}>
                  {hasResume ? <FileCheck size={14} color="#059669" /> : <FileX size={14} color="#dc2626" />}
                  <span>{hasResume ? 'Resume Pre-parsed & Ready' : 'No Resume Found'}</span>
                </div>
              </div>

                {aiUsage >= aiLimit ? (
                  <div className="ai-limit-reached-box">
                    You have reached your daily limit of {aiLimit} AI Agent sessions.<br /><br />Please try again tomorrow.
                  </div>
                ) : interviewStep === 'hardware' ? (
                  <HardwareCheck 
                    onComplete={startLiveInterview} 
                    onCancel={() => setInterviewStep('setup')} 
                  />
                ) : (
                  <form onSubmit={handleSubmit} className="ai-start-form">
                    <div className="tutor-form-inputs" style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                      {/* Interview Stage Selector */}
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary, #0f172a)', fontSize: '0.875rem' }}>
                          <Layers size={16} style={{ color: '#3b82f6' }} /> Interview Stage <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        {/* Segmented Control */}
                        <div style={{
                          display: 'flex',
                          background: 'var(--bg-card-alt, #f1f5f9)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderRadius: '12px',
                          padding: '4px',
                          gap: '3px'
                        }}>
                          {['Basic', 'Pro', 'Enterprise'].map((id) => {
                            const rawAllocated = studentProfile?.allocated_stages || (studentProfile?.allocated_stage ? studentProfile.allocated_stage.split(',').map(s => s.trim()).filter(Boolean) : ['Basic']);
                            const allocatedStages = Array.isArray(rawAllocated) ? rawAllocated : [rawAllocated];
                            const isLocked = !allocatedStages.includes(id);

                            const stageTooltips = {
                              Basic: <span><strong style={{ color: '#0f172a' }}>Basic:</strong> Standard mock interview assessment without companion tools.</span>,
                              Pro: <span><strong style={{ color: '#0f172a' }}>Pro:</strong> Includes <span style={{ color: '#818cf8', fontWeight: '600' }}>AI Resume Scorer</span> to benchmark your resume before the interview.</span>,
                              Enterprise: <span><strong style={{ color: '#0f172a' }}>Enterprise:</strong> Includes <span style={{ color: '#818cf8', fontWeight: '600' }}>AI Resume Scorer</span> &amp; <span style={{ color: '#f59e0b', fontWeight: '600' }}>AI Counsellor</span> career preparation guidance.</span>,
                            };

                            return (
                              <div key={id} style={{ flex: 1, position: 'relative' }}>
                                <button
                                  type="button"
                                  onClick={() => handleStageChange(id)}
                                  onMouseEnter={() => setHoveredStage(id)}
                                  onMouseLeave={() => setHoveredStage(null)}
                                  title={isLocked ? `Locked by your institution. You are allocated to: ${allocatedStages.join(', ')}.` : undefined}
                                  style={{
                                    width: '100%',
                                    padding: '10px 8px',
                                    borderRadius: '8px',
                                    background: stage === id ? '#3b82f6' : 'transparent',
                                    color: stage === id ? '#fff' : isLocked ? 'var(--text-muted, #94a3b8)' : 'var(--text-secondary, #64748b)',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: stage === id ? '700' : '500',
                                    fontSize: '0.85rem',
                                    letterSpacing: stage === id ? '0.02em' : '0',
                                    transition: 'all 0.2s ease',
                                    boxShadow: stage === id ? '0 2px 8px rgba(59,130,246,0.30)' : 'none',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    opacity: isLocked && stage !== id ? 0.75 : 1
                                  }}
                                >
                                  <span>{id}</span>
                                  {isLocked && <Lock size={12} style={{ opacity: 0.75 }} />}
                                </button>

                                {/* Hover Tooltip */}
                                {hoveredStage === id && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: 'calc(100% + 8px)',
                                    left: id === 'Basic' ? '0' : id === 'Enterprise' ? 'auto' : '50%',
                                    right: id === 'Enterprise' ? '0' : 'auto',
                                    transform: (id === 'Basic' || id === 'Enterprise') ? 'none' : 'translateX(-50%)',
                                    zIndex: 999,
                                    background: 'var(--bg-card, #fff)',
                                    border: '1px solid rgba(59, 130, 246, 0.25)',
                                    borderRadius: '8px',
                                    padding: '9px 13px',
                                    fontSize: '0.8rem',
                                    color: 'var(--text-secondary, #64748b)',
                                    lineHeight: '1.45',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    pointerEvents: 'none',
                                    width: 'max-content',
                                    maxWidth: '380px',
                                    whiteSpace: 'normal',
                                  }}>
                                    {stageTooltips[id]}
                                    {/* Arrow */}
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: id === 'Basic' ? '32px' : id === 'Enterprise' ? 'auto' : '50%',
                                      right: id === 'Enterprise' ? '32px' : 'auto',
                                      transform: (id === 'Basic' || id === 'Enterprise') ? 'none' : 'translateX(-50%)',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderTop: '6px solid rgba(59, 130, 246, 0.25)',
                                    }} />
                                    <div style={{
                                      position: 'absolute',
                                      top: 'calc(100% - 1px)',
                                      left: id === 'Basic' ? '32px' : id === 'Enterprise' ? 'auto' : '50%',
                                      right: id === 'Enterprise' ? '32px' : 'auto',
                                      transform: (id === 'Basic' || id === 'Enterprise') ? 'none' : 'translateX(-50%)',
                                      width: 0,
                                      height: 0,
                                      borderLeft: '5px solid transparent',
                                      borderRight: '5px solid transparent',
                                      borderTop: '5px solid var(--bg-card, #fff)',
                                    }} />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* AI Resume Scorer Feature (Available in Pro and Enterprise) */}
                      {(stage === 'Pro' || stage === 'Enterprise') && (
                        <div style={{
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderLeft: '4px solid #818cf8',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          background: 'var(--bg-card-alt, #f8fafc)',
                          boxShadow: '0 2px 8px rgba(129, 140, 248, 0.08)'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              <FileCheck size={15} style={{ color: '#818cf8', flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-primary, #0f172a)' }}>
                                  AI Resume Scorer{' '}<span style={{ fontSize: '0.72rem', color: '#818cf8', fontWeight: '500' }}>· Unlocked</span>
                                </span>
                                <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)' }}>Benchmark your resume before the interview.</p>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {scorerResult && (
                                <button
                                  type="button"
                                  onClick={() => setShowScorerModal(true)}
                                  style={{
                                    background: 'rgba(129, 140, 248, 0.1)',
                                    color: '#6366f1',
                                    border: '1px solid rgba(129, 140, 248, 0.3)',
                                    borderRadius: '6px',
                                    padding: '5px 11px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    transition: 'all 0.15s',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseOver={e => { e.currentTarget.style.background = 'rgba(129, 140, 248, 0.2)'; }}
                                  onMouseOut={e => { e.currentTarget.style.background = 'rgba(129, 140, 248, 0.1)'; }}
                                >
                                  <FileText size={11} /> View Results
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={handleRunScorer}
                                disabled={runningScorer}
                                style={{
                                  background: 'transparent',
                                  color: runningScorer ? '#475569' : '#818cf8',
                                  border: `1px solid ${runningScorer ? 'var(--border-color, #e2e8f0)' : 'var(--border-strong, #cbd5e1)'}`,
                                  borderRadius: '6px',
                                  padding: '5px 11px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: runningScorer ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.15s',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                onMouseOver={e => { if (!runningScorer) { e.currentTarget.style.borderColor = '#818cf8'; e.currentTarget.style.background = 'rgba(129,140,248,0.08)'; } }}
                                onMouseOut={e => { e.currentTarget.style.borderColor = runningScorer ? 'var(--border-color, #e2e8f0)' : 'var(--border-strong, #cbd5e1)'; e.currentTarget.style.background = 'transparent'; }}
                              >
                                {runningScorer
                                  ? <><Loader2 size={11} className="scorer-spinner" /> Analyzing...</>
                                  : <><Sparkles size={11} /> {scorerResult ? 'Re-score' : 'Run Scorer'}</>
                                }
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* AI Counsellor Feature (Available exclusively when Enterprise is selected) */}
                      {stage === 'Enterprise' && (
                        <div style={{
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderLeft: '4px solid #f59e0b',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          background: 'var(--bg-card-alt, #f8fafc)',
                          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.08)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              <GraduationCap size={15} style={{ color: '#f59e0b', flexShrink: 0 }} />
                              <div style={{ minWidth: 0 }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-primary, #0f172a)' }}>
                                  AI Counsellor{' '}<span style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: '500' }}>· Enterprise only</span>
                                </span>
                                <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)' }}>Career counselling session before your interview.</p>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <input
                                ref={counsellorReportInputRef}
                                type="file"
                                accept=".pdf,.docx,.doc,.txt"
                                style={{ display: 'none' }}
                                onChange={handleCounsellorReportSelect}
                              />
                              <button
                                type="button"
                                onClick={() => counsellorReportInputRef.current?.click()}
                                style={{
                                  background: counsellorReportFile ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.08)',
                                  color: counsellorReportFile ? '#10b981' : '#3b82f6',
                                  border: `1px solid ${counsellorReportFile ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.25)'}`,
                                  borderRadius: '6px',
                                  padding: '5px 11px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.15s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="Upload a previous AI interview report for the counsellor to reference"
                              >
                                {counsellorReportFile ? <FileCheck size={11} /> : <FileText size={11} />}
                                {counsellorReportFile ? 'Report Attached' : 'Upload Report'}
                              </button>

                              <button
                                type="button"
                                onClick={handleLaunchCounsellor}
                                disabled={launchingCounsellor}
                                style={{
                                  background: 'transparent',
                                  color: launchingCounsellor ? '#475569' : '#f59e0b',
                                  border: `1px solid ${launchingCounsellor ? 'var(--border-color, #e2e8f0)' : 'var(--border-strong, #cbd5e1)'}`,
                                  borderRadius: '6px',
                                  padding: '5px 11px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: launchingCounsellor ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'all 0.15s',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                                onMouseOver={e => { if (!launchingCounsellor) { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.background = 'rgba(245,158,11,0.08)'; } }}
                                onMouseOut={e => { e.currentTarget.style.borderColor = launchingCounsellor ? 'var(--border-color, #e2e8f0)' : 'var(--border-strong, #cbd5e1)'; e.currentTarget.style.background = 'transparent'; }}
                              >
                                {launchingCounsellor
                                  ? <><Loader2 size={11} className="animate-spin" /> Launching...</>
                                  : <><GraduationCap size={11} /> Open Session</>
                                }
                              </button>
                            </div>
                          </div>

                          {counsellorReportFile && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              background: 'rgba(16, 185, 129, 0.08)',
                              border: '1px solid rgba(16, 185, 129, 0.25)',
                              fontSize: '0.78rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
                                <FileCheck size={13} style={{ color: '#10b981', flexShrink: 0 }} />
                                <span style={{ fontWeight: '600', color: 'var(--text-primary, #0f172a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {counsellorReportFile.name}
                                </span>
                                <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.72rem', flexShrink: 0 }}>
                                  (Counsellor will analyze this report)
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => setCounsellorReportFile(null)}
                                title="Remove report"
                                style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                              >
                                <X size={13} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Difficulty Level Option - Right Above Target Job Role */}
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary, #0f172a)', fontSize: '0.875rem' }}>
                          <BarChart2 size={16} style={{ color: '#3b82f6' }} /> Difficulty Level <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        {/* Difficulty Segmented Control */}
                        <div style={{
                          display: 'flex',
                          background: 'var(--bg-card-alt, #f1f5f9)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderRadius: '12px',
                          padding: '4px',
                          gap: '3px'
                        }}>
                          {['Basic', 'Intermediate', 'Hard'].map((lvl) => {
                            const levelTooltips = {
                              Basic: <span><strong style={{ color: '#3b82f6' }}>Basic:</strong> Foundational entry-level questions covering core concepts, terminology, and standard behavioral questions.</span>,
                              Intermediate: <span><strong style={{ color: '#3b82f6' }}>Intermediate:</strong> Mid-level practical questions evaluating situational problem-solving, methodologies, and hands-on competencies.</span>,
                              Hard: <span><strong style={{ color: '#3b82f6' }}>Hard:</strong> Rigorous senior-level technical depth probing complex scenarios, system architecture, edge cases, and in-depth resume projects.</span>,
                            };

                            return (
                              <div key={lvl} style={{ flex: 1, position: 'relative' }}>
                                <button
                                  type="button"
                                  onClick={() => handleLevelChange(lvl)}
                                  onMouseEnter={() => setHoveredLevel(lvl)}
                                  onMouseLeave={() => setHoveredLevel(null)}
                                  style={{
                                    width: '100%',
                                    padding: '10px 6px',
                                    borderRadius: '8px',
                                    background: level === lvl ? '#3b82f6' : 'transparent',
                                    color: level === lvl ? '#fff' : 'var(--text-secondary, #64748b)',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: level === lvl ? '700' : '500',
                                    fontSize: '0.85rem',
                                    letterSpacing: level === lvl ? '0.02em' : '0',
                                    transition: 'all 0.2s ease',
                                    boxShadow: level === lvl ? '0 2px 8px rgba(59,130,246,0.30)' : 'none',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {lvl}
                                </button>

                                {/* Hover Tooltip */}
                                {hoveredLevel === lvl && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: 'calc(100% + 8px)',
                                    left: lvl === 'Basic' ? '0' : lvl === 'Hard' ? 'auto' : '50%',
                                    right: lvl === 'Hard' ? '0' : 'auto',
                                    transform: (lvl === 'Basic' || lvl === 'Hard') ? 'none' : 'translateX(-50%)',
                                    zIndex: 999,
                                    background: 'var(--bg-card, #fff)',
                                    border: '1px solid rgba(59, 130, 246, 0.25)',
                                    borderRadius: '8px',
                                    padding: '9px 13px',
                                    fontSize: '0.8rem',
                                    color: 'var(--text-secondary, #64748b)',
                                    lineHeight: '1.45',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                    pointerEvents: 'none',
                                    width: 'max-content',
                                    maxWidth: '380px',
                                    whiteSpace: 'normal',
                                  }}>
                                    {levelTooltips[lvl]}
                                    {/* Arrow border */}
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: lvl === 'Basic' ? '32px' : lvl === 'Hard' ? 'auto' : '50%',
                                      right: lvl === 'Hard' ? '32px' : 'auto',
                                      transform: (lvl === 'Basic' || lvl === 'Hard') ? 'none' : 'translateX(-50%)',
                                      width: 0, height: 0,
                                      borderLeft: '6px solid transparent',
                                      borderRight: '6px solid transparent',
                                      borderTop: '6px solid rgba(59, 130, 246, 0.25)',
                                    }} />
                                    {/* Arrow fill */}
                                    <div style={{
                                      position: 'absolute',
                                      top: 'calc(100% - 1px)',
                                      left: lvl === 'Basic' ? '32px' : lvl === 'Hard' ? 'auto' : '50%',
                                      right: lvl === 'Hard' ? '32px' : 'auto',
                                      transform: (lvl === 'Basic' || lvl === 'Hard') ? 'none' : 'translateX(-50%)',
                                      width: 0, height: 0,
                                      borderLeft: '5px solid transparent',
                                      borderRight: '5px solid transparent',
                                      borderTop: '5px solid var(--bg-card, #fff)',
                                    }} />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>


                      <div className="form-group custom-role-input-container" style={{ margin: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary, #0f172a)', fontSize: '0.875rem' }}>
                          <Briefcase size={16} style={{ color: '#3b82f6' }} /> Target Job Role <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <div style={{
                            position: 'absolute',
                            left: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                            color: '#94a3b8'
                          }}>
                            <Briefcase size={17} />
                          </div>
                          <input
                            type="text"
                            placeholder="e.g. Data Analyst, Frontend Developer, Product Manager..."
                            value={selectedJobRole}
                            onChange={(e) => setSelectedJobRole(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px 16px 12px 42px',
                              borderRadius: '10px',
                              background: 'var(--bg-card-alt, #ffffff)',
                              border: '1px solid var(--border-color, #cbd5e1)',
                              color: 'var(--text-primary, #0f172a)',
                              fontSize: '0.92rem',
                              fontWeight: '500',
                              boxSizing: 'border-box',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
                              outline: 'none',
                              transition: 'all 0.2s ease'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#3b82f6';
                              e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.15)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = 'var(--border-color, #cbd5e1)';
                              e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.03)';
                            }}
                          />
                        </div>

                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #94a3b8)', marginTop: '6px', display: 'block' }}>
                          Enter the specific job role you are targeting. Questions will be tailored to this role.
                        </span>
                      </div>

                      {/* Job Description Section */}
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary, #0f172a)', fontSize: '0.875rem' }}>
                          <FileText size={16} style={{ color: '#8b5cf6' }} /> Job Description
                        </label>

                        {/* JD Mode Segmented Control */}
                        <div style={{
                          display: 'flex',
                          background: 'var(--bg-card-alt, #f1f5f9)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          borderRadius: '12px',
                          padding: '4px',
                          gap: '3px',
                          marginBottom: '10px'
                        }}>
                          {[
                            { key: 'none', label: 'Not Provided' },
                            { key: 'text', label: 'Paste Text' },
                            { key: 'file', label: 'Upload File' }
                          ].map(({ key, label }) => (
                            <div key={key} style={{ flex: 1, position: 'relative' }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setJdInputMode(key);
                                  setJdMismatchError('');
                                  if (key === 'none') { setJdText(''); setJdFile(null); setJdFileName(''); }
                                }}
                                onMouseEnter={() => setHoveredJdMode(key)}
                                onMouseLeave={() => setHoveredJdMode(null)}
                                style={{
                                  width: '100%',
                                  padding: '10px 6px',
                                  borderRadius: '8px',
                                  background: jdInputMode === key ? '#3b82f6' : 'transparent',
                                  color: jdInputMode === key ? '#fff' : 'var(--text-secondary, #64748b)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontWeight: jdInputMode === key ? '700' : '500',
                                  fontSize: '0.83rem',
                                  letterSpacing: jdInputMode === key ? '0.02em' : '0',
                                  transition: 'all 0.2s ease',
                                  boxShadow: jdInputMode === key ? '0 2px 8px rgba(59,130,246,0.30)' : 'none',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {label}
                              </button>

                              {/* Tooltip for Not Provided only */}
                              {key === 'none' && hoveredJdMode === 'none' && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: 'calc(100% + 8px)',
                                  left: '0',
                                  transform: 'none',
                                  zIndex: 999,
                                  background: 'var(--bg-card, #fff)',
                                  border: '1px solid rgba(59, 130, 246, 0.25)',
                                  borderRadius: '8px',
                                  padding: '9px 13px',
                                  fontSize: '0.8rem',
                                  color: 'var(--text-secondary, #64748b)',
                                  lineHeight: '1.45',
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                  pointerEvents: 'none',
                                  width: 'max-content',
                                  maxWidth: '380px',
                                  whiteSpace: 'normal',
                                }}>
                                  <span>AI will generate questions based on your <strong style={{ color: 'var(--text-primary, #0f172a)' }}>Job Role</strong> and <strong style={{ color: 'var(--text-primary, #0f172a)' }}>Resume</strong> only.</span>
                                  {/* Arrow border */}
                                  <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '32px',
                                    transform: 'none',
                                    width: 0, height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid rgba(59, 130, 246, 0.25)',
                                  }} />
                                  {/* Arrow fill */}
                                  <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% - 1px)',
                                    left: '32px',
                                    transform: 'none',
                                    width: 0, height: 0,
                                    borderLeft: '5px solid transparent',
                                    borderRight: '5px solid transparent',
                                    borderTop: '5px solid var(--bg-card, #fff)',
                                  }} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>


                        {jdInputMode === 'text' && (
                          <div style={{ position: 'relative' }}>
                            <textarea
                              value={jdText}
                              onChange={(e) => { setJdText(e.target.value); if (jdMismatchError) setJdMismatchError(''); }}
                              placeholder="Paste the Job Description here. The AI will use these responsibilities, requirements, and skills to frame targeted interview questions..."
                              rows={6}
                              style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: '10px',
                                background: 'var(--bg-card-alt, #f8fafc)',
                                border: '1px solid var(--border-color, #cbd5e1)',
                                color: 'var(--text-primary, #0f172a)',
                                fontSize: '0.88rem',
                                lineHeight: '1.55',
                                resize: 'vertical',
                                boxSizing: 'border-box',
                                outline: 'none',
                                fontFamily: 'inherit',
                                transition: 'border-color 0.2s ease'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                              onBlur={(e) => e.target.style.borderColor = 'var(--border-color, #cbd5e1)'}
                            />
                            {jdText && (
                              <button
                                type="button"
                                onClick={() => setJdText('')}
                                style={{
                                  position: 'absolute', top: '8px', right: '8px',
                                  background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
                                  color: '#f87171', borderRadius: '6px', padding: '3px 6px',
                                  cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px'
                                }}
                              >
                                <X size={11} /> Clear
                              </button>
                            )}
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #64748b)', marginTop: '6px', display: 'block' }}>
                              {jdText.length} characters pasted
                            </span>
                          </div>
                        )}

                        {/* File Upload Mode */}
                        {jdInputMode === 'file' && (
                          <div>
                            <input
                              ref={jdFileInputRef}
                              type="file"
                              accept=".pdf,.doc,.docx,.txt"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setJdFile(file);
                                  setJdFileName(file.name);
                                  if (jdMismatchError) setJdMismatchError('');
                                }
                              }}
                            />
                            {!jdFile ? (
                              <div
                                onClick={() => jdFileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDraggingJd(true); }}
                                onDragLeave={() => setIsDraggingJd(false)}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  setIsDraggingJd(false);
                                  const file = e.dataTransfer.files?.[0];
                                  if (file) {
                                    setJdFile(file);
                                    setJdFileName(file.name);
                                    if (jdMismatchError) setJdMismatchError('');
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '24px 16px',
                                  borderRadius: '12px',
                                  border: isDraggingJd ? '2px dashed #3b82f6' : '1.5px dashed rgba(59, 130, 246, 0.4)',
                                  background: isDraggingJd ? 'rgba(59, 130, 246, 0.1)' : 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, var(--bg-card-alt, #f8fafc) 100%)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '10px',
                                  transition: 'all 0.2s ease',
                                  boxSizing: 'border-box'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isDraggingJd) {
                                    e.currentTarget.style.borderColor = '#3b82f6';
                                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.06)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isDraggingJd) {
                                    e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, var(--bg-card-alt, #f8fafc) 100%)';
                                  }
                                }}
                              >
                                <div style={{
                                  width: '44px',
                                  height: '44px',
                                  borderRadius: '12px',
                                  background: 'rgba(59, 130, 246, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#3b82f6'
                                }}>
                                  <UploadCloud size={22} />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <span style={{ display: 'block', fontWeight: '600', fontSize: '0.88rem', color: 'var(--text-primary, #0f172a)' }}>
                                    Click to upload or drag &amp; drop Job Description
                                  </span>
                                  <span style={{ display: 'block', marginTop: '3px', fontSize: '0.76rem', color: 'var(--text-secondary, #64748b)' }}>
                                    PDF, DOCX, DOC, or TXT · Max 5 MB
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                background: 'rgba(59, 130, 246, 0.05)',
                                border: '1px solid rgba(59, 130, 246, 0.25)',
                                gap: '12px'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                                  <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    background: 'rgba(16, 185, 129, 0.12)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}>
                                    <FileCheck size={18} color="#10b981" />
                                  </div>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <span style={{
                                      display: 'block',
                                      fontWeight: '600',
                                      color: 'var(--text-primary, #0f172a)',
                                      fontSize: '0.88rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {jdFileName}
                                    </span>
                                    <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary, #64748b)' }}>
                                      {jdFile ? `${(jdFile.size / 1024).toFixed(1)} KB · ` : ''}Ready to be analyzed by AI
                                    </span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => { setJdFile(null); setJdFileName(''); if (jdFileInputRef.current) jdFileInputRef.current.value = ''; }}
                                  title="Remove document"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#94a3b8',
                                    cursor: 'pointer',
                                    padding: '6px',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#f87171'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* JD–Role Mismatch Error Banner */}
                      {jdMismatchError && (
                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: '10px',
                          padding: '12px 14px', borderRadius: '10px',
                          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                          marginTop: '-4px'
                        }}>
                          <X size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: '2px' }} />
                          <div>
                            <p style={{ margin: 0, fontWeight: '700', fontSize: '0.85rem', color: '#dc2626' }}>
                              Job Description Mismatch
                            </p>
                            <p style={{ margin: '3px 0 0', fontSize: '0.82rem', color: '#b91c1c', lineHeight: '1.5' }}>
                              {jdMismatchError}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setJdMismatchError('')}
                            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px', flexShrink: 0 }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}


                      <div style={{
                        borderRadius: '12px',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        background: 'linear-gradient(135deg, rgba(6,182,212,0.04) 0%, var(--bg-card-alt, #f8fafc) 100%)',
                        overflow: 'hidden'
                      }}>
                        {/* Header */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          borderBottom: additionalDocs.length > 0 || true ? '1px solid rgba(6,182,212,0.12)' : 'none',
                          gap: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                            {/* Icon badge */}
                            <div style={{
                              width: '32px', height: '32px', borderRadius: '8px',
                              background: 'rgba(6,182,212,0.12)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <FileText size={15} style={{ color: '#06b6d4' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: '700', fontSize: '0.855rem', color: 'var(--text-primary, #0f172a)', letterSpacing: '0.01em' }}>
                                  Supporting Documents
                                </span>
                                <span style={{
                                  fontSize: '0.68rem', fontWeight: '600', color: '#64748b',
                                  background: 'rgba(100,116,139,0.1)', borderRadius: '4px',
                                  padding: '1px 6px', letterSpacing: '0.04em', textTransform: 'uppercase'
                                }}>
                                  Optional
                                </span>
                              </div>
                              <p style={{ margin: '1px 0 0', fontSize: '0.74rem', color: 'var(--text-secondary, #64748b)', lineHeight: '1.35' }}>
                                Certifications, cover letters, portfolios — AI will reference these alongside your resume.
                              </p>
                            </div>
                          </div>

                          {/* Slot counter */}
                          <div style={{
                            flexShrink: 0,
                            display: 'flex', alignItems: 'center', gap: '4px',
                            background: additionalDocs.length === 3 ? 'rgba(16,185,129,0.1)' : 'rgba(6,182,212,0.1)',
                            border: `1px solid ${additionalDocs.length === 3 ? 'rgba(16,185,129,0.3)' : 'rgba(6,182,212,0.25)'}`,
                            borderRadius: '20px', padding: '3px 10px 3px 7px',
                          }}>
                            {[0,1,2].map(i => (
                              <div key={i} style={{
                                width: '7px', height: '7px', borderRadius: '50%',
                                background: i < additionalDocs.length
                                  ? (additionalDocs.length === 3 ? '#10b981' : '#06b6d4')
                                  : 'rgba(100,116,139,0.25)',
                                transition: 'background 0.2s'
                              }} />
                            ))}
                            <span style={{
                              marginLeft: '5px', fontSize: '0.72rem', fontWeight: '700',
                              color: additionalDocs.length === 3 ? '#059669' : '#0891b2'
                            }}>
                              {additionalDocs.length}/3
                            </span>
                          </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <input
                            ref={additionalDocsInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.txt"
                            style={{ display: 'none' }}
                            onChange={handleAdditionalDocsSelect}
                          />

                          {/* Uploaded file chips */}
                          {additionalDocs.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {additionalDocs.map((doc, idx) => (
                                <div key={idx} style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '7px 10px', borderRadius: '8px',
                                  background: 'rgba(6, 182, 212, 0.07)',
                                  border: '1px solid rgba(6, 182, 212, 0.2)',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                                    <div style={{
                                      width: '22px', height: '22px', borderRadius: '6px',
                                      background: 'rgba(16,185,129,0.12)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                    }}>
                                      <Check size={12} color="#10b981" />
                                    </div>
                                    <span style={{
                                      fontWeight: '600', fontSize: '0.8rem',
                                      color: 'var(--text-primary, #0f172a)',
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                    }}>
                                      {doc.name}
                                    </span>
                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary, #64748b)', flexShrink: 0 }}>
                                      {(doc.size / 1024).toFixed(1)} KB
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAdditionalDoc(idx)}
                                    title="Remove document"
                                    style={{
                                      background: 'none', border: 'none', color: '#94a3b8',
                                      cursor: 'pointer', padding: '3px', display: 'flex',
                                      alignItems: 'center', borderRadius: '4px', marginLeft: '6px',
                                      transition: 'color 0.15s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Upload button */}
                          {additionalDocs.length < 3 && (
                            <button
                              type="button"
                              onClick={() => additionalDocsInputRef.current?.click()}
                              style={{
                                width: '100%',
                                padding: '10px 14px',
                                borderRadius: '8px',
                                border: '1.5px dashed rgba(6, 182, 212, 0.35)',
                                background: 'transparent',
                                color: '#0891b2',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '7px',
                                fontSize: '0.81rem',
                                fontWeight: '600',
                                transition: 'all 0.2s',
                                letterSpacing: '0.01em'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.background = 'rgba(6,182,212,0.07)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(6,182,212,0.35)'; e.currentTarget.style.background = 'transparent'; }}
                            >
                              <UploadCloud size={15} />
                              {additionalDocs.length === 0 ? 'Attach a Document  (PDF, DOCX, TXT)' : `Attach Another  (${3 - additionalDocs.length} slot${3 - additionalDocs.length !== 1 ? 's' : ''} left)`}
                            </button>
                          )}
                        </div>
                      </div>

                    </div>

                    <button type="submit" className="ai-start-submit-btn" disabled={submitted}>
                      {submitted ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Preparing Interview Session...</span>
                        </>
                      ) : (
                        <>
                          <Rocket size={18} />
                          <span>Launch AI Interview</span>
                        </>
                      )}
                    </button>

                    <p style={{
                      textAlign: 'left',
                      marginTop: '6px',
                      marginBottom: 0,
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary, #94a3b8)',
                      lineHeight: '1.4'
                    }}>
                      Calibrated in real-time to your target role, difficulty level, and parsed credentials.
                    </p>
                  </form>

                )}
              </div>
            </div>
          </div>
        </div>

        {/* FULL WIDTH PERFORMANCE REPORT DISPLAYED BELOW THE GRID */}
        <div style={{ marginTop: 0, width: '100%' }}>
          <PerformanceReport
              reportData={latestReport}
              mode={latestReport?.mode || mode}
              loading={loadingReport}
              onRefresh={() => fetchLatestReport(latestReport?.mode || mode)}
              onSelectReport={(rep) => setLatestReport(rep)}
              showHistoryInitially={showHistoryInitially}
              onCloseHistory={() => setShowHistoryInitially(false)}
              onCloseReport={() => setLatestReport(null)}
            />
          </div>

        <AvatarModal
          showAvatar={showAvatar}
          setShowAvatar={setShowAvatar}
          mode={mode}
          avatarUrl={avatarUrl}
          setFormData={setFormData}
          setSubmitted={setSubmitted}
          currentSessionId={currentSessionId}
          sessionMeta={sessionMeta}
          onSessionEnded={handleSessionEnded}
        />

        {/* Companion AI Counsellor Avatar Modal */}
        {showCounsellorAvatar && (
          <AvatarModal
            showAvatar={showCounsellorAvatar}
            setShowAvatar={setShowCounsellorAvatar}
            mode="counsellor"
            avatarUrl={counsellorAvatarUrl}
            currentSessionId={counsellorSessionId}
            sessionMeta={null}
            onSessionEnded={(resData) => {
              setShowCounsellorAvatar(false);
              if (resData && (resData.report || resData.final_index)) {
                const reportContent = resData.report || resData.final_index;
                setLatestReport({
                  ...resData,
                  sessionId: resData.sessionId || counsellorSessionId,
                  mode: 'counsellor',
                  createdAt: resData.createdAt || new Date().toISOString(),
                  completedAt: resData.completedAt || new Date().toISOString(),
                  studentName: resData.studentName || studentProfile?.full_name || studentProfile?.name,
                  institutionName: resData.institutionName || studentProfile?.institution_name || studentProfile?.university_name,
                  usn: resData.usn || studentProfile?.usn,
                  report: reportContent
                });
                showToast("AI Counselling session completed! Your guidance report is ready.", "info");
              } else {
                fetchLatestReport('counsellor');
              }
            }}
          />
        )}

        {/* AI Resume Scorer Popup Modal with Blurred Backdrop */}
        {showScorerModal && scorerResult && (
          <div
            className="scorer-modal-overlay"
            onClick={() => setShowScorerModal(false)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="scorer-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <ResumeScorerResults
                scorerResult={scorerResult}
                setScorerResult={setScorerResult}
                onClose={() => setShowScorerModal(false)}
                studentProfile={studentProfile}
              />
            </div>
          </div>
        )}

        {toast && (
          <div style={{
            position: 'fixed', top: '80px', right: '24px', zIndex: 9999,
            background: toast.type === 'error' ? 'rgba(255, 79, 107, 0.12)' : 'rgba(34, 217, 143, 0.12)',
            color: toast.type === 'error' ? '#ff4f6b' : '#22d98f',
            border: `1px solid ${toast.type === 'error' ? 'rgba(255, 79, 107, 0.2)' : 'rgba(34, 217, 143, 0.2)'}`,
            padding: '12px 20px', borderRadius: '8px', fontWeight: 500,
            boxShadow: '0 4px 6px rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            {toast.message}
          </div>
        )}

        {/* Optionally display Chatbot globally if needed, or disable if avatars handle everything */}
        <Chatbot hidden={showAvatar || showCounsellorAvatar} />
      </div>
    </div>
  );

  if (embedded) return content;
  return <StudentLayout activeTab="ai-agents">{content}</StudentLayout>;
}

export default SidebarAiInterviewer;
